import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MobileShell from "@/components/MobileShell";
import AdminDisplayPreference, { adminShellClassName, useAdminDisplayPreference } from "@/components/AdminDisplayPreference";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import VideoPreview from "@/components/VideoPreview";
import CopyForNotesButton from "@/components/CopyForNotesButton";
import AdminHomeButton from "@/components/AdminHomeButton";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Archive, Mail, Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { resolvePlanPrescription } from "@/lib/patientPrescription";

const STATUS_OPTIONS = ["draft", "active", "completed", "archived"];
const PLAN_TYPES = [
  { value: "program", label: "Program" },
  { value: "home_plan", label: "Home Plan" },
];
const CONDITION_GROUPS = [
  { value: "knee_oa", label: "Knee OA" },
  { value: "acl_rehab", label: "ACL Rehab" },
  { value: "low_back", label: "Low Back Pain" },
  { value: "shoulder_rehab", label: "Shoulder Rehab" },
  { value: "ankle_sprain", label: "Ankle Sprain" },
  { value: "hip_pain", label: "Hip Pain" },
];
const BAND_COLOURS = ["None", "Yellow", "Red", "Green", "Blue", "Black", "Other"];

const textValue = (value) => String(value ?? "").trim();

export default function AdminPatientPlans() {
  const { user } = useAuth() || {};
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [plans, setPlans] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [rehabPrograms, setRehabPrograms] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("");
  const [selectedExerciseIds, setSelectedExerciseIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingPatientSettings, setSavingPatientSettings] = useState(false);
  const [patientSettings, setPatientSettings] = useState({ assigned_rehab_program: "none", show_google_review_prompt: false });
  const [displayPreference, setDisplayPreference] = useAdminDisplayPreference();
  const [form, setForm] = useState(getEmptyPlanForm());
  const [addForm, setAddForm] = useState(getEmptyAddForm());
  const [exerciseCondition, setExerciseCondition] = useState("knee_oa");

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) || null,
    [plans, selectedPlanId],
  );
  const selectedPrescriptions = useMemo(
    () => (selectedPlan?.exercises || []).filter((row) => row.is_active).map(resolvePlanPrescription),
    [selectedPlan],
  );
  const canManagePatientSettings = !user || user.role === "admin" || patient?.assigned_therapist_id === user.id;
  const filteredExercises = useMemo(
    () => exercises.filter((exercise) => exerciseCondition === "all" || exercise.condition === exerciseCondition),
    [exercises, exerciseCondition],
  );
  const filteredPlaylists = useMemo(
    () => playlists.filter((playlist) => exerciseCondition === "all" || playlist.condition === exerciseCondition),
    [playlists, exerciseCondition],
  );
  const selectedPlaylist = useMemo(
    () => playlists.find((playlist) => playlist.id === selectedPlaylistId) || null,
    [playlists, selectedPlaylistId],
  );
  const selectedAddExercise = useMemo(
    () => exercises.find((exercise) => exercise.id === addForm.exercise_id) || null,
    [exercises, addForm.exercise_id],
  );

  const loadAll = useCallback(async () => {
    try {
      const [patientRes, plansRes, exercisesRes, playlistsRes, rehabProgramsRes] = await Promise.all([
        api.get(`/admin/user/${patientId}`),
        api.get(`/admin/user/${patientId}/plans`),
        api.get("/admin/exercises"),
        api.get("/admin/playlists"),
        api.get("/admin/rehab-programs"),
      ]);
      setPatient(patientRes.data.user);
      setPatientSettings({
        assigned_rehab_program: patientRes.data.user.assigned_rehab_program || "none",
        show_google_review_prompt: !!patientRes.data.user.show_google_review_prompt,
      });
      setPlans(plansRes.data);
      setExercises(exercisesRes.data.filter((exercise) => exercise.is_active));
      setPlaylists(playlistsRes.data.filter((playlist) => playlist.is_active !== false));
      setRehabPrograms(rehabProgramsRes.data);
      if (plansRes.data.length) setSelectedPlanId(plansRes.data[0].id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Unable to load plans.");
    }
  }, [patientId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!selectedPlan) return;
    setForm({
      title: selectedPlan.title || "",
      condition: selectedPlan.condition || "knee_oa",
      body_area: selectedPlan.body_area || "",
      status: selectedPlan.status || "draft",
      plan_type: selectedPlan.plan_type || "program",
      review_date: selectedPlan.review_date || "",
      notes: selectedPlan.notes || "",
    });
    setExerciseCondition(selectedPlan.condition || "knee_oa");
  }, [selectedPlan]);

  useEffect(() => {
    setSelectedPlaylistId("");
    setSelectedExerciseIds([]);
  }, [exerciseCondition]);

  const createPlan = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/user/${patientId}/plans`, form);
      toast.success("Plan created");
      setPlans((current) => [data, ...current]);
      setSelectedPlanId(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const savePatientSettings = async () => {
    setSavingPatientSettings(true);
    try {
      const { data } = await api.put(`/admin/patients/${patientId}/rehab-settings`, {
        assigned_rehab_program: patientSettings.assigned_rehab_program === "none" ? null : patientSettings.assigned_rehab_program,
        show_google_review_prompt: patientSettings.show_google_review_prompt,
      });
      setPatient((current) => ({ ...current, ...data }));
      setPatientSettings({
        assigned_rehab_program: data.assigned_rehab_program || "none",
        show_google_review_prompt: !!data.show_google_review_prompt,
      });
      toast.success("Patient rehab settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPatientSettings(false);
    }
  };

  const savePlan = async () => {
    if (!selectedPlan) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/plans/${selectedPlan.id}`, form);
      toast.success("Plan saved");
      setPlans((current) => current.map((plan) => (plan.id === data.id ? data : plan)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const archivePlan = async () => {
    if (!selectedPlan || !window.confirm("Archive this plan?")) return;
    try {
      const { data } = await api.post(`/admin/plans/${selectedPlan.id}/archive`);
      toast.success("Plan archived");
      setPlans((current) => current.map((plan) => (plan.id === data.id ? data : plan)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const addExercise = async () => {
    if (!selectedPlan || !addForm.exercise_id) return;
    try {
      const { data } = await api.post(`/admin/plans/${selectedPlan.id}/exercises`, addForm);
      toast.success("Exercise added");
      setPlans((current) => current.map((plan) => (plan.id === data.id ? data : plan)));
      setAddForm(getEmptyAddForm());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const togglePlaylistExercise = (exerciseId, checked) => {
    setSelectedExerciseIds((current) => {
      if (checked) return current.includes(exerciseId) ? current : [...current, exerciseId];
      return current.filter((id) => id !== exerciseId);
    });
  };

  const addSelectedExercises = async () => {
    if (!selectedPlan || selectedExerciseIds.length === 0) return;
    try {
      const { data } = await api.post(`/admin/plans/${selectedPlan.id}/exercises/bulk`, {
        exercise_ids: selectedExerciseIds,
        band_colour: addForm.band_colour,
      });
      toast.success("Selected exercises added");
      setPlans((current) => current.map((plan) => (plan.id === data.id ? data : plan)));
      setSelectedExerciseIds([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const updatePlanExercise = async (planExerciseId, patch) => {
    if (!selectedPlan) return;
    try {
      const { data } = await api.put(`/admin/plans/${selectedPlan.id}/exercises/${planExerciseId}`, patch);
      setPlans((current) => current.map((plan) => (plan.id === data.id ? data : plan)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const removePlanExercise = async (planExerciseId) => {
    if (!selectedPlan || !window.confirm("Remove this exercise from the plan?")) return;
    try {
      const { data } = await api.delete(`/admin/plans/${selectedPlan.id}/exercises/${planExerciseId}`);
      toast.success("Exercise removed");
      setPlans((current) => current.map((plan) => (plan.id === data.id ? data : plan)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openHandout = (mode) => {
    if (!selectedPlan || !selectedPrescriptions.length) return;
    navigate(`/admin/exercise-programs/new?patient=${encodeURIComponent(patientId)}&plan=${encodeURIComponent(selectedPlan.id)}&mode=${mode}`);
  };

  return (
    <MobileShell contentClassName={adminShellClassName(displayPreference)}>
      <header className="px-6 pt-8 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <button onClick={() => navigate(-1)} className="text-slate-500 mb-3" data-testid="plans-back-btn"><ArrowLeft size={20} /></button>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Exercise plans</p>
            <h1 className="text-2xl font-bold font-display text-slate-900 mt-1">{patient?.name || "Patient"}</h1>
            <p className="text-sm text-slate-500 mt-1">{patient?.email}</p>
          </div>
          <div className="flex flex-wrap gap-2"><AdminHomeButton /><AdminDisplayPreference value={displayPreference} onChange={setDisplayPreference} /></div>
        </div>
      </header>

      <section className="px-6 pb-8 grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)_360px] lg:items-start">
        <aside className="space-y-4 lg:sticky lg:top-4">
        {canManagePatientSettings && <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3" data-testid="patient-rehab-settings">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Patient access</div>
            <p className="mt-1 text-xs text-slate-500">Choose the recovery pathway this patient can view.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="assigned-rehab-program">Assigned Rehab Program</label>
            <Select
              value={patientSettings.assigned_rehab_program}
              onValueChange={(value) => setPatientSettings((current) => ({ ...current, assigned_rehab_program: value }))}
            >
              <SelectTrigger id="assigned-rehab-program" className="mt-1 h-11 rounded-xl" data-testid="assigned-rehab-program">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {rehabPrograms.map((program) => (
                  <SelectItem key={program.slug} value={program.slug}>{program.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
            <Checkbox
              checked={patientSettings.show_google_review_prompt}
              onCheckedChange={(checked) => setPatientSettings((current) => ({ ...current, show_google_review_prompt: !!checked }))}
              data-testid="google-review-toggle"
              className="mt-0.5"
            />
            <span><span className="block font-medium text-slate-900">Google Review Request</span>Show Google Review request to this patient</span>
          </label>
          <Button type="button" size="sm" onClick={savePatientSettings} disabled={savingPatientSettings} data-testid="save-patient-rehab-settings">
            {savingPatientSettings ? "Saving..." : "Save patient access"}
          </Button>
        </div>}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Plan details</div>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Plan title" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Select value={form.plan_type} onValueChange={(value) => setForm({ ...form, plan_type: value })}>
              <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Plan type" /></SelectTrigger>
              <SelectContent>
                {PLAN_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
              <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              value={form.condition}
              onValueChange={(value) => {
                setForm({ ...form, condition: value });
                setExerciseCondition(value);
              }}
            >
              <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Condition" /></SelectTrigger>
              <SelectContent>
                {CONDITION_GROUPS.map((condition) => <SelectItem key={condition.value} value={condition.value}>{condition.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={form.body_area} onChange={(e) => setForm({ ...form, body_area: e.target.value })} placeholder="Body area" />
          </div>
          <Input value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} placeholder="Review date (optional)" />
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal plan notes (not shown to patient)" rows={3} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={selectedPlan ? savePlan : createPlan} disabled={saving || !form.title.trim()} size="sm">
              {selectedPlan ? "Save plan" : "Create plan"}
            </Button>
            <Button onClick={() => { setSelectedPlanId(""); setForm(getEmptyPlanForm()); }} variant="outline" size="sm">
              <Plus size={16} /> New
            </Button>
            {selectedPlan && selectedPlan.status !== "archived" && (
              <Button onClick={archivePlan} variant="destructive" size="sm">
                <Archive size={16} /> Archive
              </Button>
            )}
          </div>
        </div>

        {plans.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-4">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Existing plans</div>
            <div className="space-y-2">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => setSelectedPlanId(plan.id)}
                  className={`w-full text-left rounded-xl border p-3 ${plan.id === selectedPlanId ? "border-teal-300 bg-teal-50" : "border-slate-100 bg-white"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-900">{plan.title}</span>
                    <span className="text-xs text-slate-500">{plan.plan_type === "home_plan" ? "home plan" : plan.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{plan.exercises?.length || 0} exercises</div>
                </button>
              ))}
            </div>
          </div>
        )}
        </aside>

        {selectedPlan && (
          <>
            <main className="space-y-3 lg:col-start-2">
              <div className="flex flex-wrap justify-end gap-2 rounded-xl border border-slate-200 bg-white p-3 lg:sticky lg:top-3 lg:z-20" data-testid="plan-action-bar">
                <CopyForNotesButton prescriptions={selectedPrescriptions} />
                <Button variant="outline" disabled={!selectedPrescriptions.length} onClick={() => openHandout("print")}><Printer size={16} /> Print Handout</Button>
                <Button disabled={!selectedPrescriptions.length} onClick={() => openHandout("email")}><Mail size={16} /> Email Handout</Button>
              </div>
              {(selectedPlan.exercises || []).filter((row) => row.is_active).map((row) => (
                <div key={row.id} className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{row.exercise?.title || "Exercise"}</div>
                      <div className="text-xs text-slate-500 mt-1">{row.exercise?.phase || "No phase"}</div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2"><CopyForNotesButton prescription={resolvePlanPrescription(row)} compact /><Button variant="outline" size="sm" onClick={() => removePlanExercise(row.id)}>Remove</Button></div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-7">
                    <Input defaultValue={row.sets} onBlur={(e) => updatePlanExercise(row.id, { sets: textValue(e.target.value) })} placeholder="Sets" />
                    <Input defaultValue={row.reps} onBlur={(e) => updatePlanExercise(row.id, { reps: textValue(e.target.value) })} placeholder="Reps" />
                    <Input defaultValue={row.hold_time} onBlur={(e) => updatePlanExercise(row.id, { hold_time: textValue(e.target.value) })} placeholder="Hold" />
                    <Input defaultValue={row.frequency} onBlur={(e) => updatePlanExercise(row.id, { frequency: textValue(e.target.value) })} placeholder="Frequency" />
                    <Select value={row.band_colour || "None"} onValueChange={(value) => updatePlanExercise(row.id, { band_colour: value })}>
                      <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Band" /></SelectTrigger>
                      <SelectContent>
                        {BAND_COLOURS.map((colour) => <SelectItem key={colour} value={colour}>{colour}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input defaultValue={row.resistance ?? ""} onBlur={(e) => updatePlanExercise(row.id, { resistance: textValue(e.target.value) })} placeholder="Resistance / load" />
                    <Input defaultValue={row.equipment ?? row.exercise?.equipment ?? ""} onBlur={(e) => updatePlanExercise(row.id, { equipment: textValue(e.target.value) })} placeholder="Equipment" />
                  </div>
                  <Textarea defaultValue={row.instructions_override} onBlur={(e) => updatePlanExercise(row.id, { instructions_override: textValue(e.target.value) })} placeholder="Instructions override" rows={2} />
                  <Textarea defaultValue={row.patient_notes || ""} onBlur={(e) => updatePlanExercise(row.id, { patient_notes: textValue(e.target.value) })} placeholder="Patient-facing notes (shown in the app and handout)" rows={2} />
                  <Textarea defaultValue={row.clinician_notes || ""} onBlur={(e) => updatePlanExercise(row.id, { clinician_notes: textValue(e.target.value) })} placeholder="Internal clinician notes (not shown to patient)" rows={2} />
                </div>
              ))}
              {(selectedPlan.exercises || []).filter((row) => row.is_active).length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">No exercises assigned yet.</div>
              )}
            </main>

            <aside className="space-y-4 lg:col-start-3 lg:sticky lg:top-4">
            <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Add from playlist</div>
              <Select value={exerciseCondition} onValueChange={setExerciseCondition}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Condition group" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All conditions</SelectItem>
                  {CONDITION_GROUPS.map((condition) => <SelectItem key={condition.value} value={condition.value}>{condition.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select
                value={selectedPlaylistId}
                onValueChange={(value) => {
                  setSelectedPlaylistId(value);
                  setSelectedExerciseIds([]);
                }}
              >
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Choose playlist" /></SelectTrigger>
                <SelectContent>
                  {filteredPlaylists.map((playlist) => (
                    <SelectItem key={playlist.id} value={playlist.id}>{playlist.title} - {playlist.level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPlaylist && (
                <div className="space-y-2">
                  {(selectedPlaylist.exercises || []).map((row) => (
                    <label key={row.id} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm">
                      <Checkbox
                        checked={selectedExerciseIds.includes(row.exercise_id)}
                        onCheckedChange={(value) => togglePlaylistExercise(row.exercise_id, !!value)}
                        className="mt-0.5 h-5 w-5"
                      />
                      <span>
                        <span className="block font-semibold text-slate-900">{row.exercise?.title || "Exercise"}</span>
                        <span className="block text-xs text-slate-500 mt-1">
                          {row.exercise?.sets || "-"} sets - {row.exercise?.reps || "-"} reps - {row.exercise?.frequency || "-"}
                        </span>
                      </span>
                    </label>
                  ))}
                  {selectedPlaylist.exercises?.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">This playlist has no exercises yet.</div>
                  )}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                <Select value={addForm.band_colour} onValueChange={(value) => setAddForm({ ...addForm, band_colour: value })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Band" /></SelectTrigger>
                  <SelectContent>
                    {BAND_COLOURS.map((colour) => <SelectItem key={colour} value={colour}>{colour}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={addSelectedExercises} disabled={selectedExerciseIds.length === 0} size="sm">
                  Add selected exercises
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Add exercise</div>
              <Select value={exerciseCondition} onValueChange={setExerciseCondition}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Condition group" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All conditions</SelectItem>
                  {CONDITION_GROUPS.map((condition) => <SelectItem key={condition.value} value={condition.value}>{condition.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={addForm.exercise_id} onValueChange={(value) => {
                const exercise = exercises.find((row) => row.id === value);
                setAddForm({ ...addForm, exercise_id: value, equipment: exercise?.equipment || "" });
              }}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Choose exercise" /></SelectTrigger>
                <SelectContent>
                  {filteredExercises.map((exercise) => <SelectItem key={exercise.id} value={exercise.id}>{exercise.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-7">
                <Input value={addForm.sets} onChange={(e) => setAddForm({ ...addForm, sets: e.target.value })} placeholder="Sets" />
                <Input value={addForm.reps} onChange={(e) => setAddForm({ ...addForm, reps: e.target.value })} placeholder="Reps" />
                <Input value={addForm.hold_time} onChange={(e) => setAddForm({ ...addForm, hold_time: e.target.value })} placeholder="Hold" />
                <Input value={addForm.frequency} onChange={(e) => setAddForm({ ...addForm, frequency: e.target.value })} placeholder="Frequency" />
                <Select value={addForm.band_colour} onValueChange={(value) => setAddForm({ ...addForm, band_colour: value })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Band" /></SelectTrigger>
                  <SelectContent>
                    {BAND_COLOURS.map((colour) => <SelectItem key={colour} value={colour}>{colour}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={addForm.resistance} onChange={(e) => setAddForm({ ...addForm, resistance: e.target.value })} placeholder="Resistance / load" />
                <Input value={addForm.equipment} onChange={(e) => setAddForm({ ...addForm, equipment: e.target.value })} placeholder="Equipment" />
              </div>
              {selectedAddExercise?.video_url && (
                <VideoPreview url={selectedAddExercise.video_url} title={selectedAddExercise.title} compact />
              )}
              <Button onClick={addExercise} disabled={!addForm.exercise_id} size="sm">Add to plan</Button>
            </div>
            </aside>
          </>
        )}
      </section>
    </MobileShell>
  );
}

function getEmptyPlanForm() {
  return { title: "", condition: "knee_oa", body_area: "", status: "draft", plan_type: "home_plan", review_date: "", notes: "" };
}

function getEmptyAddForm() {
  return {
    exercise_id: "",
    sets: "",
    reps: "",
    hold_time: "",
    frequency: "",
    resistance: "",
    equipment: "",
    band_colour: "None",
    instructions_override: "",
    safety_notes_override: "",
    patient_notes: "",
    clinician_notes: "",
  };
}
